// stores/dashboardActions.helpers.ts — pure helpers for dashboardStore actions.
//
// Extracted from dashboardActions.ts to keep that file under the 250-LOC cap.
// These are side-effect-free utilities plus the two store-aware helpers
// (ensureWritable, patchLayout) shared by every mutating action.

import type { StoreApi } from 'zustand';
import type { DashboardStore } from './dashboardStore.types';
import type {
  GridItem,
  LayoutDefinition,
  PanelConfigByType,
  PanelType,
} from '../pages/chart/modular/dashboardTypes';
import { getPanelDef } from '../pages/chart/modular/panelRegistry';

export type DashboardSet = StoreApi<DashboardStore>['setState'];
export type DashboardGet = StoreApi<DashboardStore>['getState'];

/** Short, collision-resistant id without a nanoid dependency. */
export function shortId(): string {
  return Math.random().toString(36).slice(2, 8);
}

/** Default grid geometry + seed config for a freshly-minted panel. */
export function defaultsFor(type: PanelType): {
  size: { w: number; h: number };
  min: { minW: number; minH: number };
  config: PanelConfigByType[PanelType];
} {
  const def = getPanelDef(type);
  return {
    size: def?.defaultSize ?? { w: 4, h: 6 },
    min: def?.minSize ?? { minW: 2, minH: 3 },
    // Omit `raw` (optional) rather than assign undefined — exactOptionalPropertyTypes.
    config: def?.defaultConfig ?? ({} as PanelConfigByType['unknown']),
  };
}

/** Lowest free row (first row below every existing item). */
export function nextRow(grid: GridItem[]): number {
  return grid.reduce((max, g) => Math.max(max, g.y + g.h), 0);
}

/** Deep-clone a layout under a fresh id, marking it as a writable custom. */
export function cloneLayout(
  source: LayoutDefinition,
  name: string,
): LayoutDefinition {
  const cloned = structuredClone(source) as LayoutDefinition;
  cloned.id = `custom-${shortId()}`;
  cloned.name = name;
  cloned.isPreset = false;
  cloned.createdAt = Date.now();
  return cloned;
}

/**
 * Resolve the layout to write to. Presets are read-only, so a mutating action
 * on a preset transparently clones it to a custom and points `activeLayout` at
 * the clone. Returns the writable layout's id (same id for customs, null if the
 * source layout is missing).
 */
export function ensureWritable(
  set: DashboardSet,
  get: DashboardGet,
  layoutId: string,
): string | null {
  const layout = get().layouts[layoutId];
  if (!layout) return null;
  if (!layout.isPreset) return layoutId;
  const custom = cloneLayout(layout, `${layout.name} (custom)`);
  set((s) => ({
    layouts: { ...s.layouts, [custom.id]: custom },
    activeLayout: s.activeLayout === layoutId ? custom.id : s.activeLayout,
  }));
  return custom.id;
}

/** Immutably replace one layout in the map (no-op if it is missing). */
export function patchLayout(
  set: DashboardSet,
  layoutId: string,
  fn: (l: LayoutDefinition) => LayoutDefinition,
): void {
  set((s) => {
    const layout = s.layouts[layoutId];
    if (!layout) return s;
    return { layouts: { ...s.layouts, [layoutId]: fn(layout) } };
  });
}
